import { useState, useCallback, useEffect } from 'react';
import defaultDocumentContent from '../data/defaultDocumentContent';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

function parseStoredPayload(raw) {
  if (!raw) return { content: deepClone(defaultDocumentContent), imageOverrides: {} };
  try {
    const parsed = JSON.parse(raw);
    if (parsed.meta && parsed.chapters) {
      return { content: parsed, imageOverrides: parsed.imageOverrides || {} };
    }
    if (parsed.content) {
      return {
        content: parsed.content,
        imageOverrides: parsed.imageOverrides || {},
      };
    }
  } catch { /* fall through */ }
  return { content: deepClone(defaultDocumentContent), imageOverrides: {} };
}

export default function useDocumentEditorStore(initialPayload) {
  const initial = parseStoredPayload(initialPayload);
  const [content, setContent] = useState(initial.content);
  const [imageOverrides, setImageOverrides] = useState(initial.imageOverrides);

  useEffect(() => {
    if (initialPayload === undefined) return;
    const parsed = parseStoredPayload(initialPayload);
    setContent(parsed.content);
    setImageOverrides(parsed.imageOverrides);
  }, [initialPayload]);

  const updateMeta = useCallback((key, value) => {
    setContent((prev) => ({ ...prev, meta: { ...prev.meta, [key]: value } }));
  }, []);

  const updateSection = useCallback((chapterId, sectionId, field, value) => {
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) =>
        ch.id !== chapterId ? ch : {
          ...ch,
          sections: ch.sections.map((sec) =>
            sec.id !== sectionId ? sec : { ...sec, [field]: value },
          ),
        },
      ),
    }));
  }, []);

  const updateStepItem = useCallback((chapterId, sectionId, index, value) => {
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) =>
        ch.id !== chapterId ? ch : {
          ...ch,
          sections: ch.sections.map((sec) => {
            if (sec.id !== sectionId) return sec;
            const next = { ...sec };
            if (next.steps) {
              const steps = [...next.steps];
              steps[index] = value;
              next.steps = steps;
            } else if (next.items) {
              const items = [...next.items];
              items[index] = value;
              next.items = items;
            }
            return next;
          }),
        },
      ),
    }));
  }, []);

  const addStep = useCallback((chapterId, sectionId) => {
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) =>
        ch.id !== chapterId ? ch : {
          ...ch,
          sections: ch.sections.map((sec) => {
            if (sec.id !== sectionId) return sec;
            if (sec.steps) return { ...sec, steps: [...sec.steps, ''] };
            if (sec.items) return { ...sec, items: [...sec.items, ''] };
            return sec;
          }),
        },
      ),
    }));
  }, []);

  const removeStep = useCallback((chapterId, sectionId, index) => {
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) =>
        ch.id !== chapterId ? ch : {
          ...ch,
          sections: ch.sections.map((sec) => {
            if (sec.id !== sectionId) return sec;
            const next = { ...sec };
            if (next.steps) next.steps = next.steps.filter((_, i) => i !== index);
            else if (next.items) next.items = next.items.filter((_, i) => i !== index);
            return next;
          }),
        },
      ),
    }));
  }, []);

  const moveStep = useCallback((chapterId, sectionId, fromIndex, toIndex) => {
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) => {
        if (ch.id !== chapterId) return ch;
        return {
          ...ch,
          sections: ch.sections.map((sec) => {
            if (sec.id !== sectionId) return sec;
            const arr = sec.steps || sec.items;
            if (!arr || toIndex < 0 || toIndex >= arr.length) return sec;
            const nextArr = [...arr];
            const [item] = nextArr.splice(fromIndex, 1);
            nextArr.splice(toIndex, 0, item);
            return sec.steps
              ? { ...sec, steps: nextArr }
              : { ...sec, items: nextArr };
          }),
        };
      }),
    }));
  }, []);

  const updateImage = useCallback((sectionId, src, caption) => {
    setImageOverrides((prev) => ({ ...prev, [sectionId]: { src, caption } }));
  }, []);

  const removeImageOverride = useCallback((sectionId) => {
    setImageOverrides((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  }, []);

  const resetChapter = useCallback((chapterId) => {
    const defaultCh = defaultDocumentContent.chapters.find((c) => c.id === chapterId);
    if (!defaultCh) return;
    setContent((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch) => (ch.id === chapterId ? deepClone(defaultCh) : ch)),
    }));
  }, []);

  const addChapter = useCallback(() => {
    setContent((prev) => {
      const n = prev.chapters.length + 1;
      return {
        ...prev,
        chapters: [
          ...prev.chapters,
          {
            id: `ch${n}`,
            title: `Chapter ${n}`,
            sections: [{
              id: `ch${n}-s1`,
              title: 'New Section',
              type: 'body',
              body: '',
            }],
          },
        ],
      };
    });
  }, []);

  const replaceContent = useCallback((next) => {
    setContent(next);
  }, []);

  const getSavePayload = useCallback(() => ({
    content,
    imageOverrides,
  }), [content, imageOverrides]);

  const actions = {
    updateMeta,
    updateSection,
    updateStepItem,
    addStep,
    removeStep,
    moveStep,
    updateImage,
    removeImageOverride,
    resetChapter,
    addChapter,
    replaceContent,
  };

  return {
    content,
    imageOverrides,
    actions,
    getSavePayload,
    setContent,
    setImageOverrides,
  };
}